import React, { useEffect, useState, useRef } from "react";
import { CirclePower } from "lucide-react";
import { SyncBadge } from "../../common/badges";
import BrightnessBar from "../common/brightness-bar";
import { getDisplayColor } from "../common/color-util";

const OnOffBrightnessColor1Widget = ({ widget, onValueUpdate, pattern }) => {
  // On/Off state
  const [isOn, setIsOn] = useState(false);

  // Brightness state
  const [brightness, setBrightness] = useState(0);
  const [minMax, setMinMax] = useState({ min: 0, max: 100 });
  const lastBrightnessRef = useRef(null);

  // Color state
  const [colorRgb, setColorRgb] = useState("ffffff");
  const [colorTemp, setColorTemp] = useState(2700);
  const [current, setCurrent] = useState("rgb");

  // Initialize states from widget data
  useEffect(() => {
    if (!widget) return;

    // On/Off state from $o1
    if (widget?.items?.$o1) {
      setIsOn(widget.items.$o1.value === "on");
    }

    // Brightness state from $b1
    if (widget?.items?.$b1) {
      const initialValue = Number(widget.items.$b1.value);
      const validValue = isNaN(initialValue) ? 0 : initialValue;
      setMinMax({
        min: widget.items.$b1.min ?? 0,
        max: widget.items.$b1.max ?? 100,
      });
      setBrightness(validValue);
      lastBrightnessRef.current = validValue;
    }

    // Color state from $c1, $c2, $c3
    if (widget.items?.$c1) {
      const colorValue = widget.items.$c1.value.toLowerCase() || "ffffff";
      setColorRgb(colorValue.replace(/^#/, ""));
    }

    if (widget.items?.$c2) {
      setColorTemp(Number(widget.items.$c2.value) || 2700);
    }

    if (widget.items?.$c3) {
      setCurrent(widget.items.$c3.value || "rgb");
    } else {
      // If no mode selector, determine current based on available data
      if (widget.items?.$c1 && !widget.items?.$c2) {
        setCurrent("rgb");
      } else if (widget.items?.$c2 && !widget.items?.$c1) {
        setCurrent("temperature");
      }
    }
  }, [widget]);

  // Get widget mode for color functionality
  const getWidgetMode = () => {
    if (!widget) return "all";

    const hasRgb = !!widget.items?.$c1;
    const hasTemp = !!widget.items?.$c2;

    if (!hasRgb && !hasTemp) {
      return "none";
    }

    const hasMode = !!widget.items?.$c3;

    if (hasRgb && hasTemp && hasMode) {
      return "all";
    } else if (hasRgb && !hasTemp) {
      return "rgb";
    } else if (hasTemp && !hasRgb) {
      return "temperature";
    } else {
      return "all";
    }
  };

  // Handle on/off toggle
  const handleToggle = () => {
    if (pattern && pattern.endsWith("_R")) return;

    const newValue = !isOn;
    setIsOn(newValue);

    const updatedWidget = {
      ...widget.items.$o1,
      value: newValue ? "on" : "off",
    };
    onValueUpdate(updatedWidget);
  };

  // Handle brightness change
  const handleBrightnessChange = (event, newValue) => {
    if (pattern && pattern.endsWith("_R")) return;

    event.stopPropagation();
    const adjustedValue = Math.min(Math.max(newValue, minMax.min), minMax.max);
    setBrightness(adjustedValue);
  };

  const handleBrightnessUpdate = (data) => {
    if (data === lastBrightnessRef.current) return;

    const updatedWidget = {
      ...widget.items.$b1,
      value: data,
    };
    lastBrightnessRef.current = data;
    onValueUpdate(updatedWidget);
  };

  // Get current color for display
  const getCurrentColor = () => {
    const widgetMode = getWidgetMode();
    const mode =
      widgetMode === "rgb" || current === "rgb" ? "rgb" : "temperature";

    if (widgetMode === "none") {
      return isOn ? "#FFD700" : "#B0B0B0";
    }

    return getDisplayColor(colorRgb || "ffffff", colorTemp, mode);
  };

  const disabled = pattern && pattern.endsWith("_R");
  const hasBrightness = !!widget?.items?.$b1;

  return (
    <div className="w-full h-full flex items-center relative">
      {pattern && pattern.endsWith("_W") && <SyncBadge />}

      {/* Show On/Off Button only if no brightness control */}
      {!hasBrightness && (
        <div
          className="h-full aspect-square flex items-center justify-center relative"
          style={{
            cursor: disabled ? "default" : "pointer",
            opacity: disabled ? 0.8 : 1,
          }}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleToggle();
          }}
        >
          <CirclePower
            size="100%"
            color={isOn ? getCurrentColor() : "#B0B0B0"}
            fill={isOn ? getCurrentColor() : "#B0B0B0"}
            fillOpacity={0.2}
            strokeWidth={2}
            style={{
              width: "100%",
              height: "100%",
            }}
          />
        </div>
      )}

      {/* Brightness Bar - flexible width, handles toggle when brightness exists */}
      {hasBrightness && (
        <div
          className="w-full flex items-center relative rounded-2xl overflow-hidden"
          style={{
            height: "40px",
          }}
        >
          <BrightnessBar
            value={brightness}
            onChange={handleBrightnessChange}
            disabled={disabled}
            onValueUpdate={handleBrightnessUpdate}
            onToggle={handleToggle}
            min={minMax.min}
            max={minMax.max}
            trackColor={getCurrentColor()}
            opacity={isOn ? 1 : 0.2}
          />
        </div>
      )}
    </div>
  );
};

export default OnOffBrightnessColor1Widget;
